import { validateFile } from "@/shared/files";
import { useFormikContext } from "formik";
import { ChangeEvent, useRef } from "react";
import { twMerge } from "tailwind-merge";
import { Button } from "../Button";
import { getRandomInt } from "@/shared/utilities";
import { FormikPostUpdate } from "../../Application/Feed/types";

export interface Props {
  fieldName: "gallery";
  title?: string | undefined | null;
  startIcon: React.ReactNode;
  className: string;
  titleClassName?: string;
  accept: string;
  maxFileSize: number;
  onFilesChange?: () => void;
  native?: boolean;
  disabled?: boolean;
  id?: string;
  multiple?: boolean;
}

export function UploadFiles({
  fieldName,
  title,
  startIcon,
  className,
  titleClassName,
  accept,
  maxFileSize,
  native = true,
  disabled,
  id,
  multiple,
}: Props) {
  const { values, setValues, setFieldTouched, setFieldError } =
    useFormikContext<FormikPostUpdate>();
  const inputRef = useRef<HTMLInputElement>(null);
  const setError = (message: string | undefined) =>
    setFieldError(fieldName, message);
  const clearError = () => setError(undefined);

  const handleFilesChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = values[fieldName];
    setFieldTouched(fieldName, true, false);
    const files = event.target.files;

    if (!files || files.length === 0) return;

    const fileArray = Array.from(files).map((f) => ({
      id: `uploaded-${getRandomInt(1000000)}`,
      imageUrl: URL.createObjectURL(f),
      file: f,
    }));

    const isValidFiles = fileArray.every((file) =>
      validateFile(file.file, maxFileSize, accept, setError),
    );

    if (isValidFiles) {
      const newValue = value ? [...value, ...fileArray] : fileArray;

      setValues({ ...values, gallery: newValue, attachedNftId: undefined });

      clearError();
    }

    event.target.value = "";
    return;
  };

  return (
    <Button
      onClick={() => inputRef.current?.click()}
      className={className}
      type="button"
      native={native}
      startIcon={startIcon}
      disabled={disabled}
    >
      <p
        className={twMerge(
          "hidden lg:inline text-white text-base tracking-tight ml-2",
          titleClassName,
        )}
      >
        {title}
      </p>
      <input
        type="file"
        ref={inputRef}
        onChange={(e) => handleFilesChange(e)}
        className="hidden"
        accept={accept}
        multiple={multiple}
        id={id}
      />
    </Button>
  );
}
